import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Toast } from "../../components/Toast";

type TestCase = {
  id: string;
  label: string;
  url: string;
};

type TestResult = TestCase & {
  status?: number;
  finalUrl?: string;
  redirected?: boolean;
  chain?: string;
  error?: string;
  note?: string;
};

const hasProtocol = (value: string) => /^[a-zA-Z][a-zA-Z\d+.-]*:\/\//.test(value);

const normalizeInput = (value: string): URL | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const normalized = hasProtocol(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    return new URL(normalized);
  } catch {
    return null;
  }
};

export function RedirectsTester() {
  const { t } = useTranslation();
  const [input, setInput] = useState("");
  const [results, setResults] = useState<TestResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const parsedUrl = useMemo(() => normalizeInput(input), [input]);

  const buildTestCases = (url: URL): TestCase[] => {
    const cases: TestCase[] = [];
    const seen = new Set<string>();
    const basePath = url.pathname || "/";
    const baseSearch = url.search;
    const baseHash = "";
    const baseProtocol = url.protocol;
    const baseHost = url.hostname;
    const wwwHost = baseHost.startsWith("www.") ? baseHost : `www.${baseHost}`;
    const nonWwwHost = baseHost.startsWith("www.") ? baseHost.slice(4) : baseHost;

    const makeUrl = (protocol: string, hostname: string, pathname: string) => {
      const next = new URL(url.toString());
      next.protocol = protocol;
      next.hostname = hostname;
      next.pathname = pathname;
      next.search = baseSearch;
      next.hash = baseHash;
      return next.toString();
    };

    const addCase = (id: string, label: string, value: string) => {
      if (seen.has(value)) {
        return;
      }
      seen.add(value);
      cases.push({ id, label, url: value });
    };

    addCase("entered", t("redirectsTester.tests.asEntered"), url.toString());
    addCase("http", t("redirectsTester.tests.http"), makeUrl("http:", baseHost, basePath));
    addCase("https", t("redirectsTester.tests.https"), makeUrl("https:", baseHost, basePath));

    if (baseHost !== wwwHost) {
      addCase(
        "www",
        t("redirectsTester.tests.www"),
        makeUrl(baseProtocol, wwwHost, basePath)
      );
    }
    if (baseHost !== nonWwwHost) {
      addCase(
        "nonwww",
        t("redirectsTester.tests.nonWww"),
        makeUrl(baseProtocol, nonWwwHost, basePath)
      );
    }

    const toggledSlash = basePath.endsWith("/")
      ? basePath.slice(0, -1) || "/"
      : `${basePath}/`;
    addCase(
      "trailingSlash",
      t("redirectsTester.tests.trailingSlash"),
      makeUrl(baseProtocol, baseHost, toggledSlash)
    );

    const indexPath = basePath.endsWith("/")
      ? `${basePath}index.html`
      : `${basePath}/index.html`;
    addCase(
      "indexHtml",
      t("redirectsTester.tests.indexHtml"),
      makeUrl(baseProtocol, baseHost, indexPath)
    );

    return cases;
  };

  const runTest = async (testCase: TestCase): Promise<TestResult> => {
    try {
      const response = await fetch(
        `/api/redirect-check?url=${encodeURIComponent(testCase.url)}`
      );
      const data = await response.json().catch(() => null);

      if (!response.ok || !data?.ok) {
        return {
          ...testCase,
          error: data?.error ?? t("redirectsTester.proxyFailed"),
        };
      }

      const chainCount = Array.isArray(data.chain)
        ? Math.max(data.chain.length - 1, 0)
        : undefined;

      return {
        ...testCase,
        status: data.status,
        finalUrl: data.finalUrl ?? testCase.url,
        redirected: data.redirected,
        chain: chainCount !== undefined ? String(chainCount) : undefined,
      };
    } catch {
      return {
        ...testCase,
        error: t("redirectsTester.proxyFailed"),
      };
    }
  };

  const runTests = async () => {
    if (!parsedUrl) {
      setError(t("redirectsTester.invalidUrl"));
      setResults([]);
      return;
    }

    setError(null);
    setIsRunning(true);
    const cases = buildTestCases(parsedUrl);
    const resolved = await Promise.all(cases.map((item) => runTest(item)));
    setResults(resolved);
    setIsRunning(false);
  };

  const copyResults = async () => {
    if (results.length === 0) {
      return;
    }
    const formatted = results
      .map((item) => {
        const status = item.status ? `Status: ${item.status}` : t("redirectsTester.noStatus");
        const finalUrl = item.finalUrl ? `Final: ${item.finalUrl}` : "";
        const redirected = item.redirected
          ? t("redirectsTester.redirectedYes")
          : t("redirectsTester.redirectedNo");
        const chain = item.chain ? `Chain: ${item.chain}` : "";
        const note = item.error ? `Error: ${item.error}` : item.note ? `Note: ${item.note}` : "";
        return [
          `${item.label}`,
          `URL: ${item.url}`,
          status,
          redirected,
          chain,
          finalUrl,
          note,
        ]
          .filter(Boolean)
          .join("\n");
      })
      .join("\n\n");

    try {
      await navigator.clipboard.writeText(formatted);
      setToastMessage(t("redirectsTester.copied"));
    } catch {
      setToastMessage(t("redirectsTester.copyFailed"));
    }
  };

  const openUrl = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyCurl = async (url: string) => {
    const command = `curl -I -L "${url}"`;
    try {
      await navigator.clipboard.writeText(command);
      setToastMessage(t("redirectsTester.curlCopied"));
    } catch {
      setToastMessage(t("redirectsTester.curlCopyFailed"));
    }
  };

  const statusClass = (status?: number) => {
    if (!status) {
      return "rt-status rt-status-neutral";
    }
    if (status >= 200 && status < 300) {
      return "rt-status rt-status-success";
    }
    if (status >= 300 && status < 400) {
      return "rt-status rt-status-warn";
    }
    return "rt-status rt-status-error";
  };

  return (
    <div className="redirects-tester">
      <div className="rt-card">
        <label className="rt-label" htmlFor="rt-input">
          {t("redirectsTester.inputLabel")}
        </label>
        <input
          id="rt-input"
          className="rt-input"
          type="text"
          placeholder={t("redirectsTester.inputPlaceholder")}
          value={input}
          onChange={(event) => setInput(event.target.value)}
        />
        {error && <p className="rt-error">{error}</p>}
        <div className="rt-actions">
          <button type="button" className="rt-primary-btn" onClick={runTests}>
            {isRunning ? t("redirectsTester.running") : t("redirectsTester.runTests")}
          </button>
          <button
            type="button"
            className="rt-secondary-btn"
            onClick={copyResults}
            disabled={results.length === 0}
          >
            {t("redirectsTester.copyResults")}
          </button>
        </div>
        <p className="rt-hint">{t("redirectsTester.inputHint")}</p>
      </div>

      <div className="rt-results">
        {results.length === 0 ? (
          <div className="rt-empty">{t("redirectsTester.emptyState")}</div>
        ) : (
          results.map((result) => (
            <div key={result.id} className="rt-result-card">
              <div className="rt-result-header">
                <span className="rt-result-title">{result.label}</span>
                <span className={statusClass(result.status)}>
                  {result.status ? result.status : t("redirectsTester.unknownStatus")}
                </span>
              </div>
              <div className="rt-result-line">
                <span className="rt-meta-label">{t("redirectsTester.requestUrl")}</span>
                <code className="rt-code">{result.url}</code>
              </div>
              <div className="rt-result-line">
                <span className="rt-meta-label">{t("redirectsTester.finalUrl")}</span>
                <code className="rt-code">
                  {result.finalUrl ? result.finalUrl : t("redirectsTester.noFinalUrl")}
                </code>
              </div>
              <div className="rt-meta-row">
                <span className="rt-meta-pill">
                  {result.redirected
                    ? t("redirectsTester.redirectedYes")
                    : t("redirectsTester.redirectedNo")}
                </span>
                <span className="rt-meta-pill">
                  {t("redirectsTester.chainLabel")}:{" "}
                  {result.chain ? result.chain : t("redirectsTester.chainUnknown")}
                </span>
              </div>
              <div className="rt-result-actions">
                <button
                  type="button"
                  className="rt-action-btn"
                  onClick={() => openUrl(result.url)}
                >
                  {t("redirectsTester.openUrl")}
                </button>
                <button
                  type="button"
                  className="rt-action-btn"
                  onClick={() => copyCurl(result.url)}
                >
                  {t("redirectsTester.copyCurl")}
                </button>
              </div>
              {(result.error || result.note) && (
                <p className="rt-note">{result.error ?? result.note}</p>
              )}
            </div>
          ))
        )}
      </div>

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
